import {
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from "react-hook-form";
import {
  BillingSchedule,
  SaveProgramFeeParams,
} from "../../api/services/common/models";
import {
  AddFeeOrExpenseRequestPayload,
  AddressDetailsPayloadParams,
  CoachAssignmentPayloadParams,
  CoachFunction,
  FunctionCoach,
  ParticipantPayloadParams,
  ProgramTopic,
  UpdateProgramParams,
  ViewFeeOrExpenseDetails,
} from "../../api/services/program/models";
import { OptionsDataType } from "../../components";
import {
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
} from "../../components/billingTypeConfigureTable/modelsAndSchemas/Models";
import { CURRENCY_FORMAT_MAP } from "../../components/textFieldCurrency/CurrencyUtils";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  BILLING_TYPE,
  FORM_ERROR_TIMEOUT_SHORT,
  SITE_CORE_COUNTRIES_VALUE,
} from "../../helpers/constants";
import { KeyValue } from "../../helpers/types/common";
import {
  checkValueIsZero,
  checkWithLowerCase,
  convertToUTCFormat,
  convertToUTCFormatWithTime,
  formatDate,
  getFormattedAmount,
  getFullName,
  getTaxPayloadFromTaxEntries,
  getValueOrBlank,
  getValueOrBlankArray,
  getValueWith2DecimalDigits,
  isEmpty,
  toNumber,
  toNumberOrZero,
} from "../../helpers/utils";
import {
  CoachAssignmentDetails,
  CoachAssignmentDetailsList,
} from "./components/createProgramTabs/tabs/participant/components/coachAssignment/model";
import {
  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES,
  AddOrEditExpenseOrFeeFormKey,
  ClientManaged,
  OutreachEligible,
  ParticipantTabFormType,
  ProgramDetail,
  ProgramFeeTabFormType,
  ProgramTabFormHookType,
} from "./modelsAndSchemas/Models";
import { programSiteCoreKeys } from "./siteCore/SiteCoreKeys";

export const convertFunctionsOrCoachToDropdown = (
  data: CoachFunction[] | FunctionCoach[] = [],
): OptionsDataType[] => {
  return data.map((item) => {
    if ("firstName" in item && "lastName" in item) {
      return {
        value: item.id,
        label: getFullName(item.firstName, item.lastName),
      };
    } else {
      return {
        value: item.id,
        label: item.name,
      };
    }
  });
};

export const convertCoachDataToDropdown = (
  data: CoachFunction[],
): OptionsDataType[] => {
  return data.map((item) => {
    return {
      value: item.id,
      label: item.name,
    };
  });
};

export const convertCoachAssignmentFormDataToPayload = (
  coachAssignmentFormData: CoachAssignmentDetailsList,
) => {
  return coachAssignmentFormData.map((formData) => ({
    functionId: formData.functionId,
    coachId: formData.coachId,
    assignedDate: convertToUTCFormatWithTime(formData.assignmentDate),
    coachTypeAssignmentId: formData?.coachTypeAssignmentId,
  }));
};

export const getCalculatedDefaultDuration = (
  selectedTopicIds: string[],
  topicList: ProgramTopic[],
) => {
  return topicList.reduce((maxDuration, topic) => {
    if (selectedTopicIds.includes(topic.id)) {
      const duration = topic.defaultDurationMins;
      return Math.max(maxDuration, duration);
    }
    return maxDuration;
  }, 0);
};

export const getAddFeesOrExpensePayload = (
  formValue: KeyValue,
  programId: string,
  userCountryDateFormat?: string,
): AddFeeOrExpenseRequestPayload => {
  const {
    type,
    transactionDate,
    expenseCategoryId,
    feeTypeId,
    feeOrExpenseName,
    expenseTypeId,
    expenseQuantity,
    expenseUnitPrice,
    feeOrExpenseCurrencyId,
    feeOrExpenseAmount,
    billingCurrencyExchangeRate,
    comments,
    billingAmount,
    expenseIncurredById,
    taxEntries,
  } = formValue;

  return {
    type,
    expenseDetails: {
      programId,
      transactionDate:
        transactionDate &&
        convertToUTCFormat(transactionDate, userCountryDateFormat),
      feeTypeId,
      feeName: feeOrExpenseName,
      expenseCategoryId,
      expenseTypeId,
      feeCurrencyId: feeOrExpenseCurrencyId,
      unitRate: expenseUnitPrice && Number(expenseUnitPrice),
      quantity: expenseQuantity && Number(expenseQuantity),
      feeAmount: Number(feeOrExpenseAmount),
      billingAmount: Number(getValueWith2DecimalDigits(billingAmount)),
      comments: comments,
      exchangeRate:
        billingCurrencyExchangeRate && Number(billingCurrencyExchangeRate),
      incurredById: expenseIncurredById,
    },
    taxDetails: taxEntries && getTaxPayloadFromTaxEntries(taxEntries),
  };
};

export const setFormExpenseData = (
  expenseDetails: ViewFeeOrExpenseDetails,
  setValue: UseFormSetValue<FieldValues>,
  billingCurrencyName: string,
  userCountryDateFormat?: string,
) => {
  const {
    type,
    taxDetails,
    expenseDetails: {
      feeName,
      transactionDate,
      feeType,
      exchangeRate,
      feeAmount,
      comments,
      expenseCategory,
      incurredBy,
      unitRate,
      quantity,
      expenseType,
      feeCurrency: { name: feeCurrencyName, id: feeCurrencyId },
      billingAmount,
    },
  } = expenseDetails;
  const feeAmountWithFormat = `${getFormattedAmount(
    feeAmount,
    CURRENCY_FORMAT_MAP[feeCurrencyName]?.locale,
  )}`;
  const billingAmountWithFormat = `${getFormattedAmount(
    billingAmount,
    CURRENCY_FORMAT_MAP[billingCurrencyName]?.locale,
  )}`;
  setValue(AddOrEditExpenseOrFeeFormKey.type, type);
  setValue(AddOrEditExpenseOrFeeFormKey.feeOrExpenseName, feeName);
  if (transactionDate) {
    const formattedDate = formatDate(transactionDate, userCountryDateFormat);
    setValue(AddOrEditExpenseOrFeeFormKey.transactionDate, formattedDate);
  }
  feeType && setValue(AddOrEditExpenseOrFeeFormKey.feeTypeId, feeType.id);
  setValue(AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId, feeCurrencyId);
  setValue(
    AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyName,
    feeCurrencyName,
  );
  setValue(AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmount, feeAmount);
  setValue(
    AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat,
    feeAmountWithFormat,
  );
  setValue(AddOrEditExpenseOrFeeFormKey.billingAmount, billingAmount);
  setValue(
    AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat,
    billingAmountWithFormat,
  );
  setValue(AddOrEditExpenseOrFeeFormKey.isRateConverted, true);
  if (exchangeRate) {
    setValue(
      AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate,
      exchangeRate,
    );
  }

  setValue(AddOrEditExpenseOrFeeFormKey.comments, comments);
  if (taxDetails) {
    setValue(AddOrEditExpenseOrFeeFormKey.taxEntries, taxDetails);
  }

  if (expenseCategory) {
    setValue(
      AddOrEditExpenseOrFeeFormKey.expenseCategoryId,
      expenseCategory.id,
    );
  }
  if (expenseType) {
    setValue(AddOrEditExpenseOrFeeFormKey.expenseTypeId, expenseType.id);
  }
  if (incurredBy) {
    setValue(AddOrEditExpenseOrFeeFormKey.expenseIncurredById, incurredBy.id);
  }
  if (unitRate) {
    setValue(AddOrEditExpenseOrFeeFormKey.expenseUnitPrice, unitRate);
  }
  if (quantity) {
    setValue(AddOrEditExpenseOrFeeFormKey.expenseQuantity, quantity);
  }
};

const mapBillingSchedules = (
  schedules:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[]
    | undefined,
) => {
  return schedules?.map(
    ({ description, order, amount, percentage, frequency, topicIds, id }) => ({
      id,
      description,
      order: toNumber(order),
      amount: Number(amount) > 0 ? Number(amount) : undefined,
      percentage: toNumberOrZero(percentage),
      frequency,
      topicIds,
    }),
  );
};

export const getProgramFeePayload = (
  formValue: ProgramFeeTabFormType,
): [SaveProgramFeeParams, boolean] => {
  const {
    billingTypeId,
    billingTriggerId,
    earlyPayDays,
    earlyPayPercentage,
    minimumTransferFee,
    proBonoCategoryId,
    currencyId,
    fee,
    schedulePeriod,
    feePaymentType,
    billingSchedules,
    taxEntries,
    id,
    specialBillingInstructions,
  } = formValue;

  return [
    {
      id,
      billingTypeId,
      billingTriggerId,
      currencyId,
      fee: toNumber(fee),
      minimumTransferFee:
        isEmpty(minimumTransferFee) || checkValueIsZero(minimumTransferFee)
          ? undefined
          : toNumber(minimumTransferFee),
      earlyPayDays: isEmpty(earlyPayDays) ? undefined : toNumber(earlyPayDays),
      earlyPayPercentage: isEmpty(earlyPayPercentage)
        ? undefined
        : toNumber(earlyPayPercentage),
      proBonoCategoryId,
      schedulePeriod,
      feePaymentType,
      billingSchedules: mapBillingSchedules(
        billingSchedules,
      ) as unknown as BillingSchedule[],
      taxes: getTaxPayloadFromTaxEntries(taxEntries),
      specialBillingInstructions,
    },
    Boolean(id),
  ];
};

export const isDeliveryCountryJapanNetherlands = (
  deliveryCountry: string = "",
) => {
  return (
    deliveryCountry === SITE_CORE_COUNTRIES_VALUE.JAPAN ||
    deliveryCountry === SITE_CORE_COUNTRIES_VALUE.NETHERLANDS
  );
};

export const getTabIndexes = (isEditOrViewFlow: boolean) => {
  return isEditOrViewFlow
    ? {
        PROGRAM: 0,
        PARTICIPANT: 1,
        DOCUMENTS: 2,
        PROGRAM_FEE: 3,
        FEES_AND_EXPENSES: 4,
      }
    : {
        PROGRAM: 0,
        PARTICIPANT: 1,
        DOCUMENTS: -1,
        PROGRAM_FEE: 2,
        FEES_AND_EXPENSES: 3,
      };
};

export const getTabIds = (pageId: string, isEditOrViewFlow: boolean) => {
  return isEditOrViewFlow
    ? {
        PROGRAM: `${pageId}-tab1`,
        PARTICIPANT: `${pageId}-tab2`,
        DOCUMENTS: `${pageId}-tab3`,
        PROGRAM_FEE: `${pageId}-tab4`,
        FEES_AND_EXPENSES: `${pageId}-tab5`,
      }
    : {
        PROGRAM: `${pageId}-tab1`,
        PARTICIPANT: `${pageId}-tab2`,
        DOCUMENTS: `${pageId}-unknown`,
        PROGRAM_FEE: `${pageId}-tab3`,
        FEES_AND_EXPENSES: `${pageId}-tab4`,
      };
};

export const getTabPanels = (pageId: string, isEditOrViewFlow: boolean) => {
  return isEditOrViewFlow
    ? {
        PROGRAM: `${pageId}-panel1`,
        PARTICIPANT: `${pageId}-panel2`,
        DOCUMENTS: `${pageId}-panel3`,
        PROGRAM_FEE: `${pageId}-panel4`,
        FEES_AND_EXPENSES: `${pageId}-panel5`,
      }
    : {
        PROGRAM: `${pageId}-panel1`,
        PARTICIPANT: `${pageId}-panel2`,
        DOCUMENTS: `${pageId}-unknown`,
        PROGRAM_FEE: `${pageId}-panel3`,
        FEES_AND_EXPENSES: `${pageId}-panel4`,
      };
};

export const getCoachAssignmentPayload = (
  coachToBeAssignDate: string | null,
  coachTypeAssignment?: CoachAssignmentDetails[],
  coachTypeAssignmentId?: string,
  userCountryDateFormat?: string,
): CoachAssignmentPayloadParams | null => {
  return {
    toBeAssignedDate: coachToBeAssignDate
      ? convertToUTCFormat(coachToBeAssignDate, userCountryDateFormat)
      : "",
    coachAssignmentId: coachTypeAssignmentId ?? null,
    coachTypeAssignment: coachTypeAssignment
      ? convertCoachAssignmentFormDataToPayload(coachTypeAssignment)
      : [],
  };
};

const addressDetailsApiObject = (
  participantGetValues: UseFormGetValues<ParticipantTabFormType>,
  touchedFieldsCountryName?: boolean,
): AddressDetailsPayloadParams => {
  const {
    address1,
    address2,
    address3,
    countryId,
    countryName,
    city,
    stateProvince,
    postalCode,
  } = participantGetValues();
  return {
    address1,
    address2,
    address3,
    countryId: touchedFieldsCountryName ? countryName : countryId,
    stateId: stateProvince,
    city,
    postalCode,
  };
};

// Base method to get common form data for create and edit
export const getBaseParticipantFormData = (
  participantGetValues: UseFormGetValues<ParticipantTabFormType>,
  touchedFieldsCountryName?: boolean,
  userCountryDateFormat?: string,
  isCMCProgram?: boolean,
  isTMLeaderCoachingProgram?: boolean,
): ParticipantPayloadParams => {
  const {
    dateOfBirth,
    gender,
    appendPhoneNumber,
    spouse,
    educationLevel,
    mustStartByDate,
    primaryLanguage,
    socialFund,
    nisId,
    nextContactDate,
    preferredName,
    ageRange,
    datePlanApproved,
    currentEmploymentStartDate,

    eligibility,
    outreachEligibleStatus,
    assignedOutreachConsultant,
    notificationDate,
    releaseDate,
    outreachEligibleDate,
    notificationSupportDate,
    notificationSupportStatus,
    primaryManagedStatus,
    secondaryManagedStatus,
    contactWithin24Hours,

    clientName,
    jobTitle,
    employeeId,
    employeeBand,
    totalYearsEmployed,
    occupationalFunction,
    occupationalFunctionOther,
    occupationalLevel,
    occupationalLevelOther,

    // coach Assignments
    coachToBeAssignDate,
    coachTypeAssignment,
    coachAssignmentId,

    //contact and notification
    primaryEmail,
    alternativeEmail,
    emailType1,
    emailType2,
    primaryNumber,
    primaryCountryCode,
    primaryIsoCode,
    alternativeNumber,
    alternativeCountryCode,
    alternativeIsoCode,
    concatType1,
    concatType2,
    appendPhoneNumberCountryCode,
    appendPhoneNumberIsoCode,
    participantGroupType,
    personalId,
    batchTypeKey,
    waveCohort,

    //intake
    intakeStatusId,
    biosRequestedCoachIds,
    biosDeclinedCoachIds,
    bioStatusId,
    biosSentCoachIds,
    chemistryMeetingStatusId,
    chemistryMeetingSelectedCoachIds,
    coachSelectedCoachIds,
    isCoachSelectionDeclined,
  } = participantGetValues();

  return {
    candidateInformation: {
      dateOfBirth: convertToUTCFormat(dateOfBirth, userCountryDateFormat),
      AgeRange: getValueOrBlank(ageRange),
      personalId: getValueOrBlank(personalId),

      genderKey: gender,
      spouse,
      educationLevelKey: educationLevel,
      mustStartByDate: convertToUTCFormat(
        mustStartByDate,
        userCountryDateFormat,
      ),
      primaryLanguageKey: primaryLanguage,
      nisid: nisId,
      nextContactDate: convertToUTCFormat(
        nextContactDate,
        userCountryDateFormat,
        true,
      ),
      preferredName: getValueOrBlank(preferredName),
      batchTypeKey: getValueOrBlank(batchTypeKey),
      waveCohort: getValueOrBlank(waveCohort),
    },
    participantsContactDetails: {
      contactType1: {
        contactType: getValueOrBlank(concatType1),
        ISOCode: getValueOrBlank(primaryIsoCode),
        countryCode: getValueOrBlank(primaryCountryCode),
        number: getValueOrBlank(primaryNumber),
      },
      contactType2: {
        contactType: getValueOrBlank(concatType2),
        ISOCode: getValueOrBlank(alternativeIsoCode),
        countryCode: getValueOrBlank(alternativeCountryCode),
        number: getValueOrBlank(alternativeNumber),
      },
      emailType1: {
        emailType: getValueOrBlank(emailType1),
        email: getValueOrBlank(primaryEmail),
      },
      emailType2: {
        emailType: getValueOrBlank(emailType2),
        email: getValueOrBlank(alternativeEmail),
      },
      appendNumber: {
        ISOCode: getValueOrBlank(appendPhoneNumberIsoCode),
        countryCode: getValueOrBlank(appendPhoneNumberCountryCode),
        number: getValueOrBlank(appendPhoneNumber),
      },
    },

    addressDetails: addressDetailsApiObject(
      participantGetValues,
      touchedFieldsCountryName,
    ),
    outreach: {
      eligibilityKey: eligibility,
      outreachEligibleStatus,
      assignedOutreachConsultant,
      primaryManagedStatus:
        eligibility === ClientManaged ? primaryManagedStatus : "",
      secondaryManagedStatus:
        eligibility === ClientManaged ? secondaryManagedStatus : "",
      notificationDate:
        !isCMCProgram && notificationDate
          ? convertToUTCFormat(notificationDate, userCountryDateFormat)
          : "",
      relaseDate:
        !isCMCProgram && releaseDate
          ? convertToUTCFormat(releaseDate, userCountryDateFormat)
          : "",
      outreachEligibleDate:
        eligibility === OutreachEligible && outreachEligibleDate
          ? convertToUTCFormat(outreachEligibleDate, userCountryDateFormat)
          : "",

      notificationSupportDate:
        !isCMCProgram && notificationSupportDate
          ? convertToUTCFormat(notificationSupportDate, userCountryDateFormat)
          : "",

      notificationSupportStatus: !isCMCProgram ? notificationSupportStatus : "",
      isContactInADayEnable: !isCMCProgram ? contactWithin24Hours : false,
    },
    sponsoringEmployer: {
      companyName: clientName,
      jobTitle: jobTitle,
      employeeId,
      employeeBand,
      totalYearsEmployed,
      occupationalFunction,
      occupationalFunctionOther: occupationalFunctionOther,
      occupationalLevel,
      occupationalLevelOther: occupationalLevelOther,
      socialFund: socialFund,
      participantGroupType: participantGroupType,
      datePlanApproved: convertToUTCFormat(
        datePlanApproved,
        userCountryDateFormat,
      ),
      currentEmploymentStartDate: convertToUTCFormat(
        currentEmploymentStartDate,
        userCountryDateFormat,
      ),
    },
    coachAssignment: getCoachAssignmentPayload(
      coachToBeAssignDate,
      coachTypeAssignment,
      coachAssignmentId,
      userCountryDateFormat,
    ),
    ...(isTMLeaderCoachingProgram && {
      intakeDetails: {
        intakeStatusId: getValueOrBlank(intakeStatusId),
        biosRequestedCoachIds: getValueOrBlankArray(biosRequestedCoachIds),
        biosDeclinedCoachIds: getValueOrBlankArray(biosDeclinedCoachIds),
        bioStatusId: getValueOrBlank(bioStatusId),
        biosSentCoachIds: getValueOrBlankArray(biosSentCoachIds),
        chemistryMeetingStatusId: getValueOrBlank(chemistryMeetingStatusId),
        chemistryMeetingSelectedCoachIds: getValueOrBlankArray(
          chemistryMeetingSelectedCoachIds,
        ),
        coachSelectedCoachIds: coachSelectedCoachIds
          ? [getValueOrBlank(coachSelectedCoachIds)]
          : undefined,
        isCoachSelectionDeclined: !!isCoachSelectionDeclined,
      },
    }),
  };
};

// Method to add extra fields for the edit case
export const getParticipantFormForEdit = (
  participantGetValues: UseFormGetValues<ParticipantTabFormType>,
  baseData: ParticipantPayloadParams,
  userCountryDateFormat?: string,
) => {
  const {
    preferredLanguage,
    projectedProgramEndDate,
    textSubscribedDate,
    textUnsubscribedDate,
    emailUnsubscribedDate,
    planPostedDate,
    datePlanApproved,
    currentEmploymentStartDate,
    bestMatchPreference,
    socialFund,
    participantGroupType,
    targetJobTitle,
    salaryRangeId,
    currenciesId,
    targetOccupationFunctionId,
    targetOrganisation,
    targetOccupationOtherFunction,
    targetOccupationLevelId,
    targetIndustryId,
    targetJobTypeId,
    targetLocation,
    skills,
    technicalSkills,
    participantReadyToStartJobSearch,
    participantJobSearchStartDate,
    participantNotSeekingEmployment,
    participantNotWillngToWorkRemotely,
    participantWillingToTravelForWork,
    openToRelocation,
    experis,
    manpowerEngineering,
    jeffersonWells,
    rpo,
    // coach Assignments
    coachToBeAssignDate,
    coachTypeAssignment,
    coachAssignmentId,
    //contact and notification
    nextEmail,
    isNextRegister,
    isPrimaryEmail,
  } = participantGetValues();
  return {
    ...baseData,
    candidateInformation: {
      ...baseData.candidateInformation,
      projectedProgramEndDate: convertToUTCFormat(
        projectedProgramEndDate,
        userCountryDateFormat,
      ),
      textSubscribedDate: convertToUTCFormat(
        textSubscribedDate,
        userCountryDateFormat,
      ),
      textUnsubscribedDate: convertToUTCFormat(
        textUnsubscribedDate,
        userCountryDateFormat,
      ),
      emailUnsubscribedDate: convertToUTCFormat(
        emailUnsubscribedDate,
        userCountryDateFormat,
      ),
      preferredLanguage: getValueOrBlank(preferredLanguage),
    },
    sponsoringEmployer: {
      ...baseData.sponsoringEmployer,
      planPostedDate: convertToUTCFormat(planPostedDate, userCountryDateFormat),
      datePlanApproved: convertToUTCFormat(
        datePlanApproved,
        userCountryDateFormat,
      ),
      currentEmploymentStartDate: convertToUTCFormat(
        currentEmploymentStartDate,
        userCountryDateFormat,
      ),
      bestMatchPeference: bestMatchPreference,
      socialFund: socialFund,
      participantGroupType: participantGroupType,
    },
    coachAssignment: getCoachAssignmentPayload(
      coachToBeAssignDate,
      coachTypeAssignment,
      coachAssignmentId,
      userCountryDateFormat,
    ),
    careerGoals: {
      ...baseData.careerGoals,
      targetJobTitle: targetJobTitle,
      salaryRangeId: salaryRangeId,
      currenciesId: currenciesId,
      targetOccupationFunctionId: targetOccupationFunctionId,
      targetOrganisation: targetOrganisation,
      targetOccupationOtherFunction: targetOccupationOtherFunction,
      targetOccupationLevelId: targetOccupationLevelId,
      targetIndustryId: targetIndustryId,
      targetJobTypeId: targetJobTypeId,
      targetLocation: targetLocation,
      skillIds: skills,
      technicalSkillIds: technicalSkills,
      participantNotSeekingEmployment: participantNotSeekingEmployment,
      participantNotWillngToWorkRemotely: participantNotWillngToWorkRemotely,
      participantWillingToTravelForWork: participantWillingToTravelForWork,
      openToRelocation: openToRelocation,
      experis: experis,
      manpowerEngineering: manpowerEngineering,
      jeffersonWells: jeffersonWells,
      rpo: rpo,
      isMarketReady:
        participantReadyToStartJobSearch ===
        programSiteCoreKeys.participantStartJobSearchYes,
      marketReadinessDate: convertToUTCFormat(
        participantJobSearchStartDate,
        userCountryDateFormat,
      ),
    },
    ...(isNextRegister && {
      nextNotificationPreferences: {
        EmailNotification: {
          email: nextEmail,
          isPrimary: isPrimaryEmail,
        },
      },
    }),
  };
};

export const getProgramFormValue = (
  programGetValues: UseFormGetValues<ProgramTabFormHookType>,
  programId: string,
  programDetail?: ProgramDetail,
  isCMCProgram?: boolean,
  isTMAssessmentProgram?: boolean,
  isTMLeaderCoachingProgram?: boolean,
  hasBusinessApprovalField?: boolean,
) => {
  const {
    prefix,
    firstName,
    lastName,
    programName,
    businessUnit,
    costCentre,
    poNumber,
    deliveryOffice,
    billingContact,
    hrContact,
    clientRelationShipOwner,
    projectManager,
    projectCoordinator,
    salesCreditDetails,
    additionalInvoiceContactId,
    participantManagerId,
    businessApprovalNumber,
    clientInformation1,
    clientInformation2,
    clientInformation3,
    site,
    additionalHRContactId,
    deliveryLeadEngagementManagerId,
    hiringManager,
    dossierNumber,
    actualClientId,
  } = programGetValues();

  const updateProgramParams: UpdateProgramParams = {
    id: programId,
    prefixKey: prefix,
    firstName,
    lastName,
    clientId: getValueOrBlank(programDetail?.clientId),
    countryId: getValueOrBlank(programDetail?.deliveryCountry),
    lobId: getValueOrBlank(programDetail?.lineOfBusinessId),
    programTemplateId: getValueOrBlank(programDetail?.programTemplateId),
    salesOffice: getValueOrBlank(programDetail?.salesOffice),
    keyNumber: getValueOrBlank(programDetail?.keyNumber),
    alternateDisplayName: getValueOrBlank(programDetail?.alternateDisplayName),
    opportunityId: getValueOrBlank(programDetail?.opportunityId),
    opportunityName: getValueOrBlank(programDetail?.opportunityName),
    programDetails: {
      programName,
      businessUnit,
      costCentre,
      poNumberId: poNumber,
      deliveryOffice,
      billingContact,
      hrContact,
      clientRelationShipOwner,
      projectManager,
      projectCoordinator,
      clientInformation1,
      clientInformation2,
      clientInformation3,
      site,
      dossierNumber,
      actualClientId,
    },
    contactDetails: {
      billingOffice: getValueOrBlank(programDetail?.billingOffice),
      salesOffice: getValueOrBlank(programDetail?.salesOffice),
      deliveryOfficeId: deliveryOffice,
      billingContactId: billingContact,
      hrContactId: hrContact,
      clientRelationShipOwnerId: clientRelationShipOwner,
      projectManagerId: projectManager,
      projectCoordinatorId: projectCoordinator,
      socialProgram: false,
      additionalInvoiceContactId:
        isCMCProgram || isTMAssessmentProgram || isTMLeaderCoachingProgram
          ? additionalInvoiceContactId
          : undefined,
      participantManagerId:
        isCMCProgram || isTMAssessmentProgram || isTMLeaderCoachingProgram
          ? participantManagerId
          : undefined,
      businessApprovalNumber: hasBusinessApprovalField
        ? getValueOrBlank(businessApprovalNumber)
        : undefined,
      deliveryLeadEngagementManagerId:
        isTMAssessmentProgram || isTMLeaderCoachingProgram
          ? getValueOrBlank(deliveryLeadEngagementManagerId)
          : undefined,
      hiringManager:
        isTMAssessmentProgram || isTMLeaderCoachingProgram
          ? getValueOrBlank(hiringManager)
          : undefined,
      additionalHRContactId: isTMAssessmentProgram
        ? getValueOrBlank(additionalHRContactId)
        : undefined,
    },
    salesCreditDetails: salesCreditDetails,
    isValidateName: true,
  };

  return updateProgramParams;
};

export const isValidPostalCode = (
  participantErrors: FieldErrors<ParticipantTabFormType>,
  setParticipantError: UseFormSetError<ParticipantTabFormType>,
  postalCodeFormKey: string,
) => {
  let isValid = true;

  if (participantErrors.postalCode) {
    setTimeout(() => {
      setParticipantError(
        postalCodeFormKey as keyof ParticipantTabFormType,
        participantErrors.postalCode!,
      );
    }, FORM_ERROR_TIMEOUT_SHORT);

    isValid = false;
  }
  return isValid;
};

export const BILLING_TYPE_CLEAR_ACTIONS = {
  [BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling]: [
    BILLING_TYPE.MILESTONE,
    BILLING_TYPE.SCHEDULE,
    BILLING_TYPE.ACTIVITY,
  ],
  [BILLING_DETAILS_SITE_CORE_VALUE.billingTypeActivity]: [
    BILLING_TYPE.MILESTONE,
    BILLING_TYPE.SCHEDULE,
  ],
  [BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone]: [
    BILLING_TYPE.SCHEDULE,
    BILLING_TYPE.ACTIVITY,
  ],
  [BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule]: [
    BILLING_TYPE.MILESTONE,
    BILLING_TYPE.ACTIVITY,
  ],
};

export const isProgramFeeType = (type: string) => {
  return checkWithLowerCase(
    ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_FEE,
    type,
  );
};
